import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import EventPanel from './EventPanel';
import { EVENT_FONT, eventColors } from './eventTheme';

// The brief only draws the REWARDS tab, so the INFO copy below is written to match the
// mechanics the rest of the screen shows rather than traced from a frame. Replace it once the
// design (or the event feed) supplies real rules text.
const INFO_DESIGN_WIDTH = 360;

type Props = {
	endsInLabel: string;
	pointsGoal: number;
	width: number;
};

export const EventInfoPanel = ({ endsInLabel, pointsGoal, width }: Props) => {
	const { t } = useTranslation();
	const scale = width / INFO_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);

	const lines = [
		t('T_SITE_EVENT_INFO_EARN_POINTS', {
			defaultValue: 'Every hand you play and every pot you win earns Event Points.',
		}),
		t('T_SITE_EVENT_INFO_REACH_GOAL', {
			defaultValue: 'Reach {{points}} points to clear the current milestone set.',
			points: pointsGoal.toLocaleString('en-US'),
		}),
		t('T_SITE_EVENT_INFO_FREE_TRACK', {
			defaultValue: 'Free Track rewards are yours to collect as soon as a milestone lights up.',
		}),
		t('T_SITE_EVENT_INFO_PREMIUM_TRACK', {
			defaultValue:
				'The Premium Track needs the event pass - it unlocks every tier you have already passed.',
		}),
		t('T_SITE_EVENT_INFO_UNCLAIMED', {
			defaultValue: 'Unclaimed rewards are lost when the event closes, so collect as you go.',
		}),
	];

	return (
		<EventPanel radius={10 * scale} borderWidth={Math.max(1, 1 * scale)} style={{ width }}>
			<View style={styles.inner}>
				<Text numberOfLines={1} style={styles.title}>
					{t('T_SITE_REFERRAL_HOW_IT_WORKS', { defaultValue: 'How it works' }).toUpperCase()}
				</Text>
				{lines.map(line => (
					<View key={line} style={styles.line}>
						<Text style={styles.bullet}>★</Text>
						<Text style={styles.text}>{line}</Text>
					</View>
				))}
				<Text numberOfLines={1} style={styles.footer}>
					{t('T_SITE_EVENT_ENDS_IN_LABEL', {
						defaultValue: 'Event ends in {{time}}',
						time: endsInLabel,
					})}
				</Text>
			</View>
		</EventPanel>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		inner: {
			paddingHorizontal: 12 * scale,
			paddingVertical: 14 * scale,
			gap: 8 * scale,
		},
		title: {
			color: eventColors.gold,
			fontFamily: EVENT_FONT,
			fontSize: 15 * scale,
			fontWeight: '800',
			letterSpacing: 0.5 * scale,
		},
		line: {
			flexDirection: 'row',
			gap: 8 * scale,
		},
		bullet: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT,
			fontSize: 11 * scale,
			lineHeight: 17 * scale,
		},
		text: {
			flex: 1,
			color: eventColors.label,
			fontFamily: EVENT_FONT,
			fontSize: 11.5 * scale,
			fontWeight: '600',
			lineHeight: 17 * scale,
		},
		footer: {
			marginTop: 2 * scale,
			color: eventColors.muted,
			fontFamily: EVENT_FONT,
			fontSize: 10.5 * scale,
			fontWeight: '700',
		},
	});

export default EventInfoPanel;
